import { useEffect, useRef } from 'react';
import Image from 'next/image';
import useStore from '@/store/useStore';
import { IDataShowroom, ShowroomStatusOptions } from '@/components/BuilderIO/common/CommonFields';
import beTwMerge from '@/utils/beTwMerge';
import RichText from '@/components/RichText/RichText';
import Button from '@/components/homepage/Button/Button';

interface IStoreLocatorCardActive {
  showroomSelected?: IDataShowroom | any;
  bookAppointment: (showroom: IDataShowroom) => void;
  hasBeenSelected: boolean;
  index: number;
}

export default function StoreLocatorCardActive({
  showroomSelected,
  bookAppointment,
  hasBeenSelected = false,
  index,
}: IStoreLocatorCardActive) {
  const isMobile = useStore(state => state.isMobile);
  const cardRef = useRef<any>(null);
  const isComingSoon = showroomSelected?.status === ShowroomStatusOptions.ComingSoon;
  const bookingRef = useRef<any>(null);

  useEffect(() => {
    if (hasBeenSelected && cardRef.current && index > 0) {
      const topNavBarRect: DOMRect = document.querySelector('.mobile-header-banner-height')?.getBoundingClientRect()!;

      if (window.scrollY > 0 && isMobile && topNavBarRect?.height === 46) {
        cardRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [cardRef, hasBeenSelected, isMobile]);

  useEffect(() => {
    if (bookingRef?.current) {
      const isIphone = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIphone) {
        bookingRef?.current.classList.remove('hover:tw-bg-color-dark-green-light');
      }
    }
  }, [bookingRef]);

  const getHeader = (store: IDataShowroom) => {
    if (store?.setsterLocationId === 'Virtual Appointment') {
      return (
        <a href={store?.storePageLink} className="hover:tw-no-underline">
          <p className="tw-m-0 tw-mb-[12px] tw-text-[24px] tw-font-normal tw-not-italic">Virtual Appointment</p>
        </a>
      );
    }

    return (
      <a href={store?.storePageLink} className="hover:tw-no-underline">
        <p className="tw-m-0 tw-text-[11px] tw-font-normal tw-not-italic tw-uppercase tw-tracking-[0.55px] lg:tw-text-[13px] lg:tw-tracking-[0.65px]">
          {store?.displayName2 || store?.displayName1}
        </p>
        <p className="tw-mb-[12px] tw-text-[24px] tw-font-medium tw-not-italic tw-leading-normal lg:tw-mb-[1rem] tw-capitalize">
          {store?.displayName1}
        </p>
      </a>
    );
  };

  const getLinkMap = (store: IDataShowroom) => {
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${store?.latitude},${store?.longitude}&query_place_id=${store?.googlePlacesId}`;

    return googleMapsUrl;
  };

  const openMap = (store: IDataShowroom) => {
    (window as any).dataLayer.push({
      event: 'showroom_locator',
      showroom_action: 'address map click',
      showroom_location: `${store?.displayName1}${store?.displayName2 ? ` - ${store?.displayName2}` : ''}`,
      showroom_page_section: 'main card',
    });
  };

  const addressInfo = (store: IDataShowroom) => {
    const linkMap = getLinkMap(store);

    if (isComingSoon) {
      return (
        <div className="tw-mb-[20px]">
          <RichText
            content={store?.storeDescriptionText}
            className="tw-mt-[1rem] [&]:tw-m-0 [&]:!tw-text-[14px] [&]:tw-font-book [&]:!tw-font-[350] [&]:tw-not-italic [&]:!tw-leading-[19.12px] [&>p]:tw-m-0 [&>p]:!tw-text-[14px] [&>p]:tw-font-book [&>p]:!tw-font-[350] [&>p]:tw-not-italic [&>p]:!tw-leading-[19.12px]"
          />
        </div>
      );
    }

    if (store?.addressLine1 || store?.addressLine2 || store?.city || store?.state || store?.zipCode) {
      return (
        <a
          href={linkMap}
          target="_blank"
          rel="noreferrer"
          onClick={() => openMap(store)}
          className="tw-inline-block hover:tw-no-underline"
        >
          {store?.addressLine1 || store?.addressLine2 ? (
            <p className="tw-m-0 tw-text-[14px] tw-font-book tw-font-[350] tw-not-italic tw-leading-normal">
              {`${store?.addressLine1 || ''} ${store?.addressLine2 || ''}`}
            </p>
          ) : null}

          {store?.city || store?.state || store?.zipCode ? (
            <p className="tw-m-0 tw-text-[14px] tw-font-book tw-font-[350] tw-not-italic tw-leading-normal">
              {`${store?.city ? `${store?.city},` : ''} ${store?.state || ''} ${store?.zipCode || ''}`}
            </p>
          ) : null}
        </a>
      );
    }
  };

  return (
    <div
      ref={cardRef}
      className="slda tw-bg-[#F9F6F5] lg:tw-w-[calc(100%-317px)] lg:tw-min-h-[386px] lg:tw-flex lg:tw-flex-row-reverse"
    >
      {showroomSelected && (
        <div className={beTwMerge(isComingSoon ? 'tw-flex-auto' : 'tw-flex-1')}>
          <a href={showroomSelected?.storePageLink}>
            <Image
              src={showroomSelected.mobileLocationImage || showroomSelected.locationImage}
              alt={showroomSelected?.locationImageAltText || ''}
              fetchPriority="high"
              loading="eager"
              width={343}
              height={240}
              className="tw-block md:tw-hidden tw-w-full lg:tw-max-w-[510px] tw-h-full"
            />
            <Image
              src={showroomSelected.locationImage}
              alt={showroomSelected?.locationImageAltText || ''}
              fetchPriority="high"
              loading="eager"
              width={510}
              height={431}
              className="tw-hidden md:tw-block tw-w-full lg:tw-max-w-[510px] tw-h-full"
            />
          </a>
        </div>
      )}
      {showroomSelected && (
        <div
          className={beTwMerge(
            'tw-w-full tw-px-[1rem] tw-pt-[1rem] tw-pb-[2rem] lg:tw-w-auto lg:tw-py-[2rem] lg:tw-px-[24px] lg:tw-flex lg:tw-flex-col lg:tw-w-[calc(100%-510px)]',
            showroomSelected?.setsterLocationId !== 'Virtual Appointment' && 'lg:tw-justify-between',
            isComingSoon && 'tw-flex-1'
          )}
        >
          <div>
            {showroomSelected && getHeader(showroomSelected)}

            {/* Address */}
            <div
              className={beTwMerge(
                'tw-flex tw-flex-col',
                showroomSelected?.setsterLocationId !== 'Virtual Appointment' ? 'tw-mb-[12px]' : 'tw-mb-[2rem]'
              )}
            >
              {showroomSelected && addressInfo(showroomSelected)}
              {!isComingSoon &&
                showroomSelected?.additionalAddressDescription &&
                showroomSelected?.setsterLocationId !== 'Virtual Appointment' && (
                  <p className="tw-m-0 tw-text-[14px] tw-font-book tw-font-[350] tw-not-italic tw-leading-normal">
                    {showroomSelected?.additionalAddressDescription}
                  </p>
                )}
              {!isComingSoon && showroomSelected?.setsterLocationId === 'Virtual Appointment' && (
                <RichText
                  content={showroomSelected?.storeDescriptionText}
                  className="tw-mt-[1rem] tw-m-0 !tw-text-[14px] tw-font-book !tw-font-[350] tw-not-italic tw-leading-normal [&>p]:tw-m-0 [&>p]:!tw-text-[14px] [&>p]:tw-font-book [&>p]!tw-font-[350] [&>p]:tw-not-italic [&>p]:tw-leading-normal"
                />
              )}
            </div>

            {/* Hours */}
            {!isComingSoon &&
              showroomSelected?.hours &&
              showroomSelected?.setsterLocationId !== 'Virtual Appointment' && (
                <div className="tw-flex tw-flex-col tw-mb-[2rem] lg:tw-mb-[1rem]">
                  <RichText
                    content={showroomSelected?.hours}
                    className="tw-m-0 !tw-text-[14px] tw-font-book !tw-font-[350] tw-not-italic tw-leading-normal [&>p]:tw-m-0 [&>p]:!tw-text-[14px] [&>p]:tw-font-book [&>p]:tw-font-[350] [&>p]:tw-not-italic [&>p]:tw-leading-normal"
                  />
                </div>
              )}

            {/* View All stores link - Desktop */}
            {showroomSelected?.setsterLocationId !== 'Virtual Appointment' && (
              <div className="tw-hidden tw-w-full tw-flex-col tw-items-center min-[768px]:tw-items-start lg:tw-flex lg:tw-flex-1">
                <a
                  href={showroomSelected?.storePageLink}
                  className="tw-cursor-pointer tw-underline tw-font-medium tw-leading-normal tw-text-center tw-text-[14px]"
                  onClick={() => {
                    (window as any).dataLayer.push({
                      event: 'showroom_locator',
                      showroom_action: 'view store info',
                      showroom_location: `${showroomSelected?.displayName1}${showroomSelected?.displayName2 ? ` - ${showroomSelected?.displayName2}` : ''}`,
                      showroom_page_section: 'main card',
                    });
                  }}
                >
                  View Store Info
                </a>
              </div>
            )}
          </div>

          {/* Book Appointment Button */}
          <div>
            {!isComingSoon ? (
              <Button
                ref={bookingRef}
                onClick={() => {
                  bookAppointment(showroomSelected as IDataShowroom);

                  (window as any).dataLayer.push({
                    event: 'showroom_locator',
                    showroom_action: 'book setster appointment',
                    showroom_location: `${showroomSelected?.displayName1}${showroomSelected?.displayName2 ? ` - ${showroomSelected?.displayName2}` : ''}`,
                    showroom_page_section: 'main card',
                  });
                }}
                label={
                  // eslint-disable-next-line no-nested-ternary
                  showroomSelected?.setsterLocationId === 'Virtual Appointment'
                    ? 'Book Virtual Appointment'
                    : showroomSelected?.status === ShowroomStatusOptions.PreBook
                      ? 'Pre-Book Appointment'
                      : 'Book Appointment'
                }
                className="tw-mb-[1rem] lg:tw-mb-0 lg:tw-min-w-[200px]"
              />
            ) : (
              <Button
                ref={bookingRef}
                href={showroomSelected?.storePageLink}
                label="Sign Up for Updates"
                className="tw-mb-[1rem] lg:tw-mb-0 lg:tw-min-w-[200px]"
              />
            )}
          </div>

          {/* View All stores link */}
          {showroomSelected?.setsterLocationId !== 'Virtual Appointment' && (
            <div className="tw-w-full tw-flex tw-flex-col tw-items-center min-[768px]:tw-items-start lg:tw-hidden">
              <a
                href={showroomSelected?.storePageLink}
                className="tw-cursor-pointer tw-underline tw-font-normal tw-leading-normal tw-text-center"
                onClick={() => {
                  (window as any).dataLayer.push({
                    event: 'showroom_locator',
                    showroom_action: 'view store info',
                    showroom_location: `${showroomSelected?.displayName1}${showroomSelected?.displayName2 ? ` - ${showroomSelected?.displayName2}` : ''}`,
                    showroom_page_section: 'main card',
                  });
                }}
              >
                View Store Info
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
